import React, { useState, useEffect, useCallback } from 'react';
import { FeedPost, SessionUser } from '../types';
import { MomentCard } from '../components/MomentCard';

interface Props {
  postId: string;
  onNavigate: (page: string, params?: Record<string, string>) => void;
  currentUser: SessionUser | null;
  onLoginRequired?: () => void;
}

export function PostDetailPage({ postId, onNavigate, currentUser, onLoginRequired }: Props) {
  const [post, setPost] = useState<FeedPost | null>(null);
  const [parentPost, setParentPost] = useState<FeedPost | null>(null);
  const [replies, setReplies] = useState<FeedPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [postRes, repliesRes] = await Promise.all([
        fetch(`/api/v1/posts/${postId}`),
        fetch(`/api/v1/posts/${postId}/replies`),
      ]);
      if (!postRes.ok) { setError('Post not found'); return; }
      const postJson = await postRes.json() as { data: FeedPost };
      const repliesJson = await repliesRes.json() as { data: FeedPost[] };
      const loadedPost = postJson.data;
      setPost(loadedPost);
      setReplies(repliesJson.data);
      // Fetch parent if this is a reply
      if (loadedPost.parent_post_id) {
        const parentRes = await fetch(`/api/v1/posts/${loadedPost.parent_post_id}`);
        if (parentRes.ok) {
          const parentJson = await parentRes.json() as { data: FeedPost };
          setParentPost(parentJson.data);
        }
      }
    } catch {
      setError('Failed to load post');
    } finally {
      setLoading(false);
    }
  }, [postId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!post) return;
    const track = post.spotify_track_name ?? 'a moment';
    document.title = `${track} — mylodic`;
    return () => { document.title = 'mylodic'; };
  }, [post]);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60%', color: 'var(--text-muted)' }}>
      <Spinner />
    </div>
  );
  if (error || !post) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60%', color: 'var(--text-muted)' }}>
      {error ?? 'Not found'}
    </div>
  );

  return (
    <div className="fade-in">
      {/* Header */}
      <div style={stickyHeader}>
        <button onClick={() => onNavigate('feed')} style={backBtn}>
          <ChevronLeft />
        </button>
        <span style={{ fontWeight: 700, fontSize: 16 }}>Post</span>
        <div style={{ width: 28 }} />
      </div>

      {/* Parent post (if this is a reply) */}
      {parentPost && (
        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', opacity: 0.7 }}>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
            <ReplyIcon />
            Replying to
          </div>
          <MomentCard
            post={parentPost}
            currentUserId={currentUser?.id}
            onLoginRequired={onLoginRequired}
            onResonate={() => {}}
            onReply={() => {}}
            onOpen={p => onNavigate('post-detail', { postId: p.id })}
            onNavigateUser={u => onNavigate('user', { username: u })}
          />
        </div>
      )}

      {/* Post */}
      <div style={{ padding: '12px 16px', borderBottom: '2px solid var(--border)' }}>
        <MomentCard
          post={post}
          currentUserId={currentUser?.id}
          onLoginRequired={onLoginRequired}
          onResonate={() => {}}
          onReply={p => {
            if (!currentUser) { onLoginRequired?.(); return; }
            onNavigate('post', { reply_to: p.id });
          }}
          onNavigateUser={u => onNavigate('user', { username: u })}
          onDelete={() => onNavigate('feed')}
          onUpdate={updated => setPost(updated)}
        />
      </div>

      {/* Reply button */}
      {currentUser && (
        <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)' }}>
          <button
            onClick={() => onNavigate('post', { reply_to: post.id })}
            style={replyPromptStyle}
          >
            <ReplyIcon />
            Reply…
          </button>
        </div>
      )}

      {/* Replies */}
      {replies.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={sectionLabel}>Replies · {replies.length}</div>
          {replies.map(r => (
            <div key={r.id} style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
              <MomentCard
                post={r}
                currentUserId={currentUser?.id}
                onLoginRequired={onLoginRequired}
                onResonate={() => {}}
                onReply={p => {
                  if (!currentUser) { onLoginRequired?.(); return; }
                  onNavigate('post', { reply_to: p.id });
                }}
                onOpen={p => onNavigate('post-detail', { postId: p.id })}
                onNavigateUser={u => onNavigate('user', { username: u })}
                onDelete={id => setReplies(prev => prev.filter(x => x.id !== id))}
                onUpdate={updated => setReplies(prev => prev.map(x => x.id === updated.id ? updated : x))}
              />
            </div>
          ))}
        </div>
      )}

      {replies.length === 0 && !loading && (
        <div style={{ textAlign: 'center', padding: '48px 24px', color: 'var(--text-muted)', fontSize: 14 }}>
          No replies yet.
        </div>
      )}
    </div>
  );
}

function ChevronLeft() {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6"/>
    </svg>
  );
}

function ReplyIcon() {
  return (
    <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 14l-4-4 4-4" /><path d="M5 10h11a4 4 0 010 8h-1" />
    </svg>
  );
}

function Spinner() {
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      {[0, 1, 2].map(i => (
        <div key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--accent)', animation: `pulse 1.2s ease-in-out ${i * 0.2}s infinite` }} />
      ))}
    </div>
  );
}

const stickyHeader: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '12px 16px', position: 'sticky', top: 0,
  background: 'rgba(10,10,10,0.92)', backdropFilter: 'blur(12px)',
  borderBottom: '1px solid var(--border)', zIndex: 10,
};

const backBtn: React.CSSProperties = {
  color: 'var(--text-muted)', display: 'flex', alignItems: 'center', width: 28,
};

const sectionLabel: React.CSSProperties = {
  fontSize: 12, fontWeight: 600, color: 'var(--text-muted)',
  textTransform: 'uppercase', letterSpacing: '0.05em',
  padding: '10px 16px 6px',
};

const replyPromptStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8,
  width: '100%', padding: '10px 14px',
  background: 'var(--surface)', border: '1px solid var(--border)',
  borderRadius: 'var(--radius)', color: 'var(--text-muted)',
  fontSize: 14, cursor: 'pointer', textAlign: 'left',
};
